import React from "react";
import { Breadcrumb } from "antd";
import { Link, useLocation } from "react-router-dom";
import navigationConfig from "configs/NavigationConfig";
import urls from "urls";
import { usePageCrumbValue } from "../../data/hooks/usePageCrumb";

/**
 * Breadcrumb in de vaste headerbalk, naast het meldingencentrum.
 *
 * Werkt op **langste-prefix-matching** tegen NavigationConfig, niet per
 * padsegment. Twee redenen:
 * - De eerste kruimel is een groepsnaam ("Inkoop") die niet in de URL voorkomt;
 *   die groepknopen in NavigationConfig hebben geen `path`.
 * - Tussenliggende segmenten (/cms, /cms/purchase-orders) hebben geen titel en
 *   zouden lege, klikbare kruimels opleveren.
 *
 * De oude `AppBreadcrumb.js` deed het wél per segment en was daarnaast kapot
 * (`const { location } = useLocation()` — useLocation geeft de location zélf
 * terug, dus alles was undefined). Die is vervangen door dit bestand.
 *
 * Voor nu alleen de inkoop-sectie, zoals gevraagd. Op alle andere routes geeft
 * de component `null` terug en blijft de headerbalk zoals hij was.
 */
const CRUMB_GROUPS = ["purchasing"];

// Eenmalig platslaan: per inkooppagina zijn pad, titel en de groep waar hij in
// zit. Langste pad eerst, zodat "/x/shipments" wint van een eventuele "/x".
const SECTIONS = navigationConfig
  .filter((group) => CRUMB_GROUPS.includes(group.key))
  .flatMap((group) =>
    (group.submenu || []).map((item) => ({
      path: item.path,
      title: item.title,
      group: group.title,
    })),
  )
  .filter((section) => Boolean(section.path))
  .sort((a, b) => b.path.length - a.path.length);

// Inkooporder detail is via twee paden bereikbaar: het pad uit de navigatie
// (/cms/purchase-orders/all/:id) en een ouder pad dat het meldingencentrum en de
// factuurdetail nog gebruiken (/cms/orders/purchase-orders/:id, gemount in
// views/app-views/orders/index.js). Zonder deze alias blijft de kruimel juist
// leeg wanneer je uit het meldingencentrum komt.
const ALIASES = [{ prefix: "/cms/orders/purchase-orders", sectionPath: urls.purchaseOrdersList }];

const HeaderBreadcrumb = () => {
  const { pathname } = useLocation();
  const recordLabel = usePageCrumbValue();

  const matched = SECTIONS.find((s) => pathname === s.path || pathname.startsWith(`${s.path}/`));

  const alias =
    matched ||
    (() => {
      const hit = ALIASES.find((a) => pathname === a.prefix || pathname.startsWith(`${a.prefix}/`));
      if (!hit) return null;
      const target = SECTIONS.find((s) => s.path === hit.sectionPath);
      // Via een alias is het altijd een detailweergave, ook al is het pad korter
      // dan het sectiepad — vandaar de expliciete vlag.
      return target ? { ...target, _aliasDetail: pathname !== hit.prefix } : null;
    })();

  const section = alias;
  if (!section) return null;

  const isDetail = section._aliasDetail !== undefined ? section._aliasDetail : pathname !== section.path;

  // Een item zonder href rendert in antd als platte tekst — precies de
  // niet-klikbare behandeling die het ontwerp voor de groep en het huidige
  // record laat zien. Geen eigen kleuren nodig.
  const items = [
    { title: section.group },
    isDetail ? { title: <Link to={section.path}>{section.title}</Link> } : { title: section.title },
    ...(isDetail ? [{ title: recordLabel || "…" }] : []),
  ];

  return (
    <Breadcrumb
      items={items}
      style={{
        fontSize: 13,
        // Truncatie zoals in het ontwerp: een lang recordnummer kapt zichzelf af
        // in plaats van de bel-knop uit beeld te duwen.
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: 0,
      }}
    />
  );
};

export default HeaderBreadcrumb;
